import { zodResolver } from '@hookform/resolvers/zod';
import { type RegisterRequest, registerRequestSchema } from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import type { z } from 'zod';
import { FormError } from '../components/FormError';
import { PublicLayout } from '../components/PublicLayout';
import { Button } from '../components/ui/button';
import { Field } from '../components/ui/field';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';

type RegisterForm = z.input<typeof registerRequestSchema>;

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const form = useForm<RegisterForm, unknown, z.output<typeof registerRequestSchema>>({
    resolver: zodResolver(registerRequestSchema),
  });
  const register = useMutation({
    mutationFn: (values: RegisterRequest) => api.auth.register(values),
    onSuccess: () => navigate('/members', { replace: true }),
  });
  const errors = form.formState.errors;

  return (
    <PublicLayout title={t('register.title')} intro={t('register.intro')}>
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit((v) => register.mutate(v))}
      >
        <FormError error={register.error} />
        <Field label={t('fields.displayName')} error={errors.displayName}>
          <Input autoComplete="name" {...form.register('displayName')} />
        </Field>
        <Field label={t('fields.familyName')} error={errors.familyName}>
          <Input {...form.register('familyName')} />
        </Field>
        <Field label={t('fields.email')} error={errors.email} kind="email">
          <Input type="email" autoComplete="email" inputMode="email" {...form.register('email')} />
        </Field>
        <Field
          label={t('fields.newPassword')}
          hint={t('fields.passwordHint')}
          error={errors.password}
          kind="password"
        >
          <Input type="password" autoComplete="new-password" {...form.register('password')} />
        </Field>
        <Button type="submit" block disabled={register.isPending}>
          {register.isPending ? t('register.submitting') : t('register.submit')}
        </Button>
      </form>
      <p className="text-center text-sm text-muted">
        {t('register.haveAccount')}{' '}
        <Link to="/login" className="font-medium text-accent underline">
          {t('register.login')}
        </Link>
      </p>
    </PublicLayout>
  );
}
